import type { AgendaSettings } from "@bookdental/schemas";
import { Field, SegButton, Toggle } from "./primitives";
import { ColorPickerField } from "./ColorPickerField";
import { getCardStyleSpec, type CardStyle } from "@/lib/agenda-card-styles";
import { getDentistColor } from "@/lib/selected-dentists";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";

type Props = {
  values: AgendaSettings;
  onChange: (partial: Partial<AgendaSettings>) => void;
};

type StyleOption = {
  key: CardStyle;
  label: string;
  description: string;
};

const STYLE_OPTIONS: StyleOption[] = [
  { key: "colorful", label: "Colorido", description: "Padrão — bg claro + barra lateral" },
  { key: "bordered", label: "Com bordas", description: "Borda marcada em toda a volta" },
  { key: "bordered-thin", label: "Destaque fino", description: "Borda fina — separa blocos vizinhos" },
  { key: "bordered-strong", label: "Destaque forte", description: "Borda grossa — máxima separação" },
  { key: "minimal", label: "Minimalista", description: "Fundo branco + barra lateral grossa" },
  { key: "outlined", label: "Contornado", description: "Borda fina + indicador lateral" },
  { key: "solid", label: "Sólido", description: "Fundo saturado + texto branco" },
  { key: "sticker", label: "Sticker", description: "Fundo vivo + sombra colorida" },
  { key: "glass", label: "Vidro", description: "Translúcido com blur — premium iOS/Vercel" },
  { key: "ribbon", label: "Fita superior", description: "Bg branco + fita colorida no topo" },
  { key: "light", label: "Claro", description: "Todos os cards em azul claro uniforme" },
  { key: "assorted", label: "Sortido", description: "Cor por paciente (hash do nome)" },
];

export function VisualSection({ values, onChange }: Props) {
  const timeLineOff = values.timeLineMode === "off";

  return (
    <div className="space-y-6">
      <Group title="Cards de consulta">
        <Field label="Estilo do card">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
            {STYLE_OPTIONS.map((opt) => (
              <StyleCard
                key={opt.key}
                option={opt}
                active={values.cardStyle === opt.key}
                onClick={() => onChange({ cardStyle: opt.key })}
              />
            ))}
          </div>
          <p className="mt-2 text-[11px] text-zinc-500 dark:text-muted-foreground">
            A mudança aplica imediatamente a todos os agendamentos, inclusive os
            importados.
          </p>
        </Field>

        <Field label="Linhas do grid">
          <div className="grid grid-cols-3 gap-2">
            {(["off", "subtle", "strong"] as const).map((mode) => (
              <SegButton
                key={mode}
                active={values.gridLines === mode}
                onClick={() => onChange({ gridLines: mode })}
              >
                {mode === "off"
                  ? "Sem grid"
                  : mode === "subtle"
                    ? "Grid sutil"
                    : "Grid marcado"}
              </SegButton>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-zinc-500 dark:text-muted-foreground">
            "Grid marcado" tem traço mais grosso e destaca a hora cheia —
            ajuda em monitores de baixa resolução.
          </p>
        </Field>

        <Toggle
          label="Mostrar profissional no card"
          description='Exibe "Dr. João" ou "Dra. Maria" dentro do bloco de consulta'
          checked={values.showDentistInCard}
          onChange={(v) => onChange({ showDentistInCard: v })}
        />

        <Toggle
          label="Mostrar horário no card"
          description="Exibe o intervalo (08:00 → 08:30) antes do nome do paciente"
          checked={values.showTimeInCard}
          onChange={(v) => onChange({ showTimeInCard: v })}
        />
      </Group>

      <DentistSettingsGroup values={values} onChange={onChange} />

      <Group title="Linha do horário atual">
        <Field label="Quando mostrar">
          <div className="grid grid-cols-3 gap-2">
            {(["all", "today", "off"] as const).map((mode) => (
              <SegButton
                key={mode}
                active={values.timeLineMode === mode}
                onClick={() => onChange({ timeLineMode: mode })}
              >
                {mode === "all"
                  ? "Todos os dias"
                  : mode === "today"
                    ? "Só hoje"
                    : "Desligada"}
              </SegButton>
            ))}
          </div>
        </Field>

        <Field label="Estilo do traço">
          <div className="grid grid-cols-2 gap-2">
            {(["solid", "dashed"] as const).map((style) => (
              <SegButton
                key={style}
                active={values.timeLineStyle === style}
                onClick={() => onChange({ timeLineStyle: style })}
                disabled={timeLineOff}
              >
                {style === "solid" ? "Sólida ───" : "Pontilhada — — —"}
              </SegButton>
            ))}
          </div>
        </Field>

        <Field label="Cor da linha">
          <ColorPickerField
            value={values.timeLineColor}
            onChange={(c) => onChange({ timeLineColor: c })}
            disabled={timeLineOff}
          />
        </Field>

        <Toggle
          label="Destacar linha do dia atual"
          description="Linha de hoje mais grossa com ponto marcador. Se desligado, todas ficam iguais."
          checked={values.timeLineHighlightToday}
          onChange={(v) => onChange({ timeLineHighlightToday: v })}
          disabled={timeLineOff}
        />
      </Group>
    </div>
  );
}

function StyleCard({
  option,
  active,
  onClick,
}: {
  option: StyleOption;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex flex-col gap-2 rounded-lg border p-2.5 text-left transition-all",
        active
          ? "border-brand-400 bg-brand-50/50 ring-2 ring-brand-400/40 dark:border-primary dark:bg-primary/10 dark:ring-primary/40"
          : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 dark:border-border dark:hover:border-border dark:hover:bg-muted",
      )}
    >
      <StylePreview styleKey={option.key} />
      <div>
        <div
          className={cn(
            "text-[12px] font-semibold leading-tight",
            active
              ? "text-brand-900 dark:text-primary"
              : "text-zinc-900 dark:text-foreground",
          )}
        >
          {option.label}
        </div>
        <div className="mt-0.5 text-[10px] leading-tight text-zinc-500 dark:text-muted-foreground">
          {option.description}
        </div>
      </div>
    </button>
  );
}

/** Mini preview dos 3 cards empilhados aplicando o estilo escolhido. */
function StylePreview({ styleKey }: { styleKey: CardStyle }) {
  const colors: string[] =
    styleKey === "assorted"
      ? ["#2457FF", "#10B981", "#F59E0B"]
      : styleKey === "light"
        ? ["#5BA3D9", "#5BA3D9", "#5BA3D9"]
        : ["#10B981", "#3B82F6", "#F59E0B"];

  const labels = ["Maria S.", "João P.", "Ana L."];

  return (
    <div className="flex h-[72px] flex-col gap-1 rounded-md bg-zinc-100 p-1.5 dark:bg-background">
      {colors.map((hex, i) => {
        const spec = getCardStyleSpec(hex, styleKey);
        return (
          <div
            key={i}
            className={cn(
              "flex h-5 items-center overflow-hidden rounded-[3px] px-1.5 text-[9px] font-semibold",
              spec.textClassName,
            )}
            style={spec.style}
          >
            <span className="truncate">{labels[i]}</span>
          </div>
        );
      })}
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-foreground">
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

const TITLE_OPTIONS: Array<"Dr." | "Dra." | "Prof." | "Enf." | ""> = [
  "Dr.",
  "Dra.",
  "Prof.",
  "Enf.",
  "",
];

function DentistSettingsGroup({
  values,
  onChange,
}: {
  values: AgendaSettings;
  onChange: (partial: Partial<AgendaSettings>) => void;
}) {
  const query = trpc.organization.listDentists.useQuery();
  const dentists = (query.data ?? []) as Array<{
    id: string;
    name: string;
    image: string | null;
  }>;

  if (dentists.length === 0) return null;

  const updateDentist = (
    userId: string,
    patch: { color?: string; title?: "Dr." | "Dra." | "Prof." | "Enf." | "" },
  ) => {
    const current = values.dentistSettings[userId] ?? {};
    onChange({
      dentistSettings: {
        ...values.dentistSettings,
        [userId]: { ...current, ...patch },
      },
    });
  };

  return (
    <Group title="Profissionais">
      <p className="-mt-2 text-[12px] text-zinc-500 dark:text-muted-foreground">
        Cor usada no avatar do filtro e prefixo exibido quando "Mostrar
        profissional no card" está ativo.
      </p>
      <ul className="space-y-2">
        {dentists.map((d) => {
          const pref = values.dentistSettings[d.id] ?? {};
          const color = getDentistColor(d.id, values.dentistSettings);
          const title = (pref.title ?? "Dr.") as
            | "Dr."
            | "Dra."
            | "Prof."
            | "Enf."
            | "";
          const firstName = d.name?.split(/\s+/)[0] ?? "?";
          return (
            <li
              key={d.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-border dark:bg-card"
            >
              {d.image ? (
                <img
                  src={d.image}
                  alt={d.name}
                  className="h-9 w-9 shrink-0 rounded-full object-cover ring-2"
                  style={{ borderColor: color, boxShadow: `0 0 0 2px ${color}` }}
                />
              ) : (
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-semibold text-white"
                  style={{ backgroundColor: color }}
                >
                  {d.name?.charAt(0).toUpperCase()}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">
                  {title} {firstName}
                </p>
                <p className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
                  {d.name}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={title}
                  onChange={(e) =>
                    updateDentist(d.id, {
                      title: e.target.value as typeof title,
                    })
                  }
                  className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-xs font-medium text-zinc-700 outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 dark:border-border dark:bg-background dark:text-foreground"
                >
                  {TITLE_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t === "" ? "(sem prefixo)" : t}
                    </option>
                  ))}
                </select>
                <ColorPickerField
                  value={color}
                  onChange={(c) => updateDentist(d.id, { color: c })}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </Group>
  );
}
